<script setup lang="ts">
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {SuggestionProviderItemsWithOptions, WidgetProps} from "@/utils/presenters/interfaces.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {computed, ref, useTemplateRef} from "vue";
import type {QBtnDropdown, QInput, QList} from "quasar";
import {focusAndSelectInput, type NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import Translator from "@/utils/common/Translator.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;

const props = defineProps<WidgetProps>();
const emit = defineEmits<NamaNavigateToNextEmit>()
const fieldId = props.field.id!;
const fieldUtil = new BigTextFieldWidgetUtil(props.presenter, props.fieldMetaData);
const qInputRef = useTemplateRef<QInput>("qInputRef")
const qBtnDropdownModel = ref(false)
const isSpannedLayout = props.field.layout == EditScreenFieldLayout.spanned
const canShowMicrophone = computed(() => qBtnDropdownModel.value || fieldUtil.speech.isListening.value)
const allowedValuesList = ref<SuggestionProviderItemsWithOptions[] | undefined>(undefined);
const showAllowedValuesMenu = ref(false);
const {isUltra, compactMode} = storeToRefs(useNamaFlagsStore());
const btnPadding = computed(() => isUltra.value ? '4px' : compactMode.value ? '6px' : '8px');

function focus() {
  focusAndSelectInput(qInputRef);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

async function onInputDblClick() {
  if (!allowedValuesList.value)
    await fieldUtil.suggest(fieldUtil.fetchFieldValue(), allowedValuesList);
  if (allowedValuesList.value && allowedValuesList.value.length > 0)
    showAllowedValuesMenu.value = true;
}

async function onAllowedValueSelected(value: SuggestionProviderItemsWithOptions) {
  fieldUtil.onPartialUserInput(value);
  await fieldUtil.onUserInputFinished();
  showAllowedValuesMenu.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (!(e.key == "Enter" || e.key == "Tab"))
    return;
  if (e.ctrlKey || e.altKey)
    return fieldUtil.insertNewLine(qInputRef);
  e.preventDefault();
  emit("navigateToNext", {keyboardEvent: e});
}

defineExpose({focus, canReceiveFocus})
</script>
<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget="{ showAsPassword }">
      <q-input
        outlined dense hide-bottom-space
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
        :id="field.id" ref="qInputRef"
        :type="showAsPassword ? 'password' : 'textarea'"
        autocomplete="off"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        class="nama-textarea-dense nama-focused-input control-big-text-microphone-opacity tw-overflow-hidden"
        :class="[
               {'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)},
               {'nama-customize-dense-input':!isSpannedLayout}
               ]"
        :input-class="[isSpannedLayout?'nama-input-class tw-h-16 compact:tw-h-14 ultra:tw-h-10':'tw-bg-transparent tw-font-normal tw-text-base ultra:tw-text-sm dark:tw-text-white focus:tw-ring-transparent focus:tw-ring-0','tw-px-2 ultra:tw-px-1']"
        :model-value="fieldUtil.inputFieldValueRef.value" @update:model-value="fieldUtil.onPartialUserInput" @focus="fieldUtil.onFocus"
        @blur="fieldUtil.onBlur"
        @dblclick="onInputDblClick"
        @keydown="onKeydown">
        <q-menu v-model="showAllowedValuesMenu" no-parent-event fit>
          <q-list dense ref="qListRef2">
            <q-item v-for="(val, idx) in allowedValuesList" :key="idx"
                    clickable v-close-popup
                    @click.stop.prevent="onAllowedValueSelected(fieldUtil.getSuggestionText(val))">
              <q-item-section v-bind:dir="val.optionDirection">
                <q-item-label
                  v-text="Translator.translateId(fieldUtil.getSuggestionText(val))"/>
                <q-item-label caption class="dark:tw-text-[rgb(255,255,255,0.5)]"
                              v-if="SearchingAndSuggestionUtil.hasDescriptor(val as EntityReferenceData)" v-text="val.optionCaption"/>
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
        <template #append>
          <div
            class="tw-pe-1 tw-pt-3 compact:tw-pt-2 ultra:tw-pt-1 tw-flex tw-z-10 tw-gap-2 tw-absolute tw-bottom-3 tw-right-2 ultra:tw-right-1">
            <q-btn-dropdown
              size="xs"
              :padding="btnPadding"
              split
              flat
              @beforeShow="qBtnDropdownModel=true"
              @beforeHide="qBtnDropdownModel=false"
              unelevated
              :data-big-text-is-focused="fieldUtil.isFieldFocused.value?'':null"
              :disable="fieldUtil.isFieldDisabled(forceEnableField) || !fieldUtil.speech.isSupported.value"
              :class="[canShowMicrophone?'!tw-opacity-100':'microphone']"
              class="q-btn-dropdown-style"
              @click.stop="fieldUtil.startSpeechRecognition()">
              <template #label>
                <NamaIcon
                  class="tw-size-5 compact:tw-size-[18px] ultra:tw-size-4 active-primary"
                  :default-icon="fieldUtil.speech.isListening.value?'nm-wave':'nm-microphone'"/>
              </template>
              <q-list ref="qListRef">
                <q-item dense class="tw-px-2" :class="{'selected-list-item':fieldUtil.lang.value==item.value}"
                        @click="fieldUtil.onSpeechLanguageChange(item.value)"
                        clickable
                        v-close-popup
                        v-for="item in BigTextFieldWidgetUtil.speechLanguages">
                  <q-item-section>
                    <q-item-label class="tw-text-xs" v-text="Translator.translateId(item.value)"/>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>
